import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Button, CircularProgress } from '@mui/material';
import PaymentSummary from './PaymentMethod/PaymentSummary.react';
import BankTransferDetailsPanel from './BankTransfer/BankTransferDetailsPanel.react';
import BankTransferUpload from './BankTransfer/BankTransferUpload.react';
import Ajax from '@/utils/Ajax';
import BankTransferActions from '@/actions/BankTransferActions';
import BankTransferStore from '@/stores/BankTransferStore';
import BankTransferConstants from '@/constants/BankTransferConstants';
import { getBankTransferDeadline } from '@/utils/PayLater';

const date_format = 'D MMM YYYY';

// payload: { id (payment id), booking_id, files }. Nothing is uploaded until submit
const SettlePaymentBankTransfer = ({ booking, bookings, isMultiMode, profile, chargeCurrency, onSuccess, onFilesChange }) => {
	const { t } = useTranslation();
	const bookingId = booking?.id;

	const hasResetStoreRef = useRef(false);
	if (!hasResetStoreRef.current) {
		hasResetStoreRef.current = true;
		const idsToReset = isMultiMode ? (bookings || []).map((b) => b.id) : [bookingId];
		BankTransferActions.resetBankTransferState(idsToReset);
	}

	const [bankDetails, setBankDetails] = useState(null);
	const [files, setFiles] = useState([]);
	const [status, setStatus] = useState(BankTransferStore.getStatus(bookingId));
	const [progress, setProgress] = useState(BankTransferStore.getProgress(bookingId));

	const bookingIds = useMemo(() => (isMultiMode ? (bookings || []).map((b) => b.id) : []), [isMultiMode, bookings]);
	const bookingIdsKey = bookingIds.join(',');

	const getMultiSnapshot = () =>
		bookingIds.reduce(
			(acc, id) => {
				acc.statuses[id] = BankTransferStore.getStatus(id);
				acc.progress[id] = BankTransferStore.getProgress(id);
				return acc;
			},
			{ statuses: {}, progress: {} },
		);

	const [multiSnapshot, setMultiSnapshot] = useState(getMultiSnapshot);

	const combinedDeadline = useMemo(() => {
		if (!isMultiMode) {
			return null;
		}

		const deadlines = (bookings || [])
			.map((b) => getBankTransferDeadline(b))
			.filter((deadline) => deadline instanceof Date && !isNaN(deadline));

		if (deadlines.length === 0) {
			return null;
		}

		return new Date(Math.min(...deadlines.map((deadline) => deadline.getTime())));
	}, [isMultiMode, bookings]);

	useEffect(() => {
		Ajax.getBankTransferDetails()
			.done(({ bank_details }) => setBankDetails(bank_details))
			.fail((err) => console.error('Error while fetching bank transfer details:', err));
	}, []);

	useEffect(() => {
		function handleStoreChange() {
			setStatus(BankTransferStore.getStatus(bookingId));
			setProgress(BankTransferStore.getProgress(bookingId));

			if (isMultiMode) {
				setMultiSnapshot(getMultiSnapshot());
			}
		}

		BankTransferStore.addChangeListener(handleStoreChange);
		return () => BankTransferStore.removeChangeListener(handleStoreChange);
	}, [bookingId, isMultiMode, bookingIdsKey]);

	useEffect(() => {
		if (isMultiMode) {
			return;
		}

		if (status === BankTransferConstants.STATUS.SUCCESS) {
			onSuccess?.(BankTransferStore.getRequest(bookingId));
		}
	}, [status, bookingId, isMultiMode]);

	useEffect(() => {
		if (!isMultiMode || bookingIds.length === 0) {
			return;
		}

		// One combined request covers every selected booking, so all ids settle together --
		// either every id reads SUCCESS with the same combined request, or the whole batch failed.
		const allSuccess = bookingIds.every((id) => multiSnapshot.statuses[id] === BankTransferConstants.STATUS.SUCCESS);

		if (!allSuccess) {
			return;
		}

		onSuccess?.(BankTransferStore.getRequest(bookingIds[0]));
	}, [multiSnapshot, isMultiMode, bookingIds, onSuccess]);

	useEffect(() => {
		onFilesChange?.(files.length > 0);
	}, [files.length]);

	const handleSubmit = () => {
		if (isMultiMode) {
			BankTransferActions.createBankTransferRequest({
				ids: bookings.map((b) => _.get(b, 'payment._id')),
				booking_ids: bookings.map((b) => b.id),
				files,
			});
			return;
		}

		BankTransferActions.createBankTransferRequest({
			id: _.get(booking, 'payment._id'),
			booking_id: bookingId,
			files,
		});
	};

	const multiIsSubmitting =
		isMultiMode && bookingIds.some((id) => multiSnapshot.statuses[id] === BankTransferConstants.STATUS.BUSY);
	const multiHasFailed =
		isMultiMode && bookingIds.some((id) => multiSnapshot.statuses[id] === BankTransferConstants.STATUS.FAILED);
	const multiProgressAverage =
		isMultiMode && bookingIds.length > 0
			? bookingIds.reduce((sum, id) => sum + (multiSnapshot.progress[id] || 0), 0) / bookingIds.length
			: 0;

	const isSubmitting = isMultiMode ? multiIsSubmitting : status === BankTransferConstants.STATUS.BUSY;
	const hasFailed = isMultiMode ? multiHasFailed : status === BankTransferConstants.STATUS.FAILED;
	const progressValue = isMultiMode ? multiProgressAverage : progress;
	const canSubmit = files.length > 0 && !isSubmitting;

	return (
		<div className="settle-payment-wrap">
			<div className="settle-payment bank-transfer__page">
				<PaymentSummary
					profile={profile}
					booking={booking}
					bookings={bookings}
					isMultiMode={isMultiMode}
					chargeCurrency={chargeCurrency}
				/>

				{isMultiMode && combinedDeadline && (
					<div className="bank-transfer__multi-deadline">
						{t('Bookings.SettlePayment.AvailableForTransfersUntil', {
							date: moment(combinedDeadline).format(date_format),
						})}
					</div>
				)}

				<BankTransferDetailsPanel bankDetails={bankDetails} />

				<BankTransferUpload
					files={files}
					onFilesAdded={(newFiles) => setFiles((prev) => [...prev, ...newFiles])}
					onFileRemoved={(index) => setFiles((prev) => prev.filter((_file, i) => i !== index))}
				/>

				{hasFailed && (
					<div className="bank-transfer__error-row">
						<span className="show-error">{t('Bookings.SettlePayment.BankTransferSubmitFailed')}</span>
					</div>
				)}

				<div className="actions">
					<Button
						variant="contained"
						className="bank-transfer__submit-button"
						disabled={!canSubmit}
						onClick={handleSubmit}
						aria-busy={isSubmitting}
						aria-label={isSubmitting ? t('Bookings.SettlePayment.Submitting') : undefined}
					>
						{isSubmitting ? (
							<CircularProgress size={20} color="inherit" variant="determinate" value={Math.round(progressValue * 100)} />
						) : (
							t('Bookings.SettlePayment.SubmitBankTransferRequest')
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default SettlePaymentBankTransfer;
